import React, { useState } from "react";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  InputAdornment,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import { changeDelegatePassword } from "src/DAL/Delegate";

const passwordRequirements = [
  {
    id: "minLength",
    label: "At least 8 characters",
    test: (p) => p.length >= 8,
  },
  // {
  //   id: "hasUpper",
  //   label: "One uppercase letter",
  //   test: (p) => /[A-Z]/.test(p),
  // },

  // {
  //   id: "hasSymbol",
  //   label: "One special character",
  //   test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
  // },
  {
    id: "match",
    label: "Passwords match",
    test: (p, confirm) => p === confirm && confirm.length > 0,
  },
];

const validatePassword = (password, confirmPassword = "") => {
  const validations = {};
  passwordRequirements.forEach((req) => {
    validations[req.id] =
      req.id === "match"
        ? req.test(password, confirmPassword)
        : req.test(password);
  });

  return {
    isValid: Object.values(validations).every(Boolean),
    validations,
  };
};

export default function ChangePasswordDialog({ open, onClose, delegate }) {
  const { enqueueSnackbar } = useSnackbar();
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    validations: {},
  });

  const handleTogglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const newPasswords = {
      ...passwords,
      [field]: value,
    };
    setPasswords(newPasswords);

    setPasswordValidation(
      validatePassword(newPasswords.newPassword, newPasswords.confirmPassword)
    );

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
  
    const newErrors = {};
  
    if (!passwords.newPassword) {
      newErrors.newPassword = "New password is required";
    }
  
    if (!passwords.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    }
  
    if (
      passwords.newPassword &&
      passwords.confirmPassword &&
      passwords.newPassword !== passwords.confirmPassword
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }
  
    if (passwords.newPassword && !passwordValidation.isValid) {
      newErrors.newPassword = "Password does not meet requirements";
    }
  
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    const data = {
      coach_id: delegate._id,
      password: passwords.newPassword,
      confirm_password: passwords.confirmPassword,
    };
  
    setLoading(true);
  
    try {
      const response = await changeDelegatePassword(data);
  
      if (response?.code === 200) {
        enqueueSnackbar(response.message, { variant: "success" });

        setPasswords({ newPassword: "", confirmPassword: "" });
        setPasswordValidation({ isValid: false, validations: {} });
        onClose();
      } else {
        enqueueSnackbar(response?.message || "Something went wrong", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error changing password", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };
  

  const handleClose = () => {
    setPasswords({ newPassword: "", confirmPassword: "" });
    setErrors({});
    setPasswordValidation({ isValid: false, validations: {} });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Change Password
        <IconButton onClick={handleClose}>
          <Icon icon={closeFill} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="New Password"
              type={showPasswords.new ? "text" : "password"}
              value={passwords.newPassword}
              onChange={handleChange("newPassword")}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleTogglePassword("new")}>
                      <Icon icon={showPasswords.new ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {passwords.newPassword && !passwordValidation.isValid && (
              <Box sx={{ px: 2, py: 1, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
                >
                  Password requirements:
                </Typography>
                <Stack spacing={0.5}>
                  {passwordRequirements.map((req) => (
                    <Stack
                      key={req.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: passwordValidation.validations[req.id]
                            ? "success.main"
                            : "grey.400",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: passwordValidation.validations[req.id]
                            ? "success.main"
                            : "text.secondary",
                        }}
                      >
                        {req.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPasswords.confirm ? "text" : "password"}
              value={passwords.confirmPassword}
              onChange={handleChange("confirmPassword")}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleTogglePassword("confirm")}>
                      <Icon
                        icon={showPasswords.confirm ? eyeFill : eyeOffFill}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Changing..." : "Change Password"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
